import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DollarSign, TrendingUp, ShieldCheck, GraduationCap, Home } from 'lucide-react';
import type { OccupationScores } from '@/hooks/useOccupationEnrichment';

interface OccupationScoreBadgesProps {
  scores: OccupationScores;
  className?: string;
}

interface ScoreDef {
  key: keyof OccupationScores;
  label: string;
  icon: typeof DollarSign;
}

const SCORE_DEFS: ScoreDef[] = [
  { key: 'pay_score', label: 'Pay', icon: DollarSign },
  { key: 'growth_score', label: 'Growth', icon: TrendingUp },
  { key: 'ai_safety_score', label: 'AI-Safe', icon: ShieldCheck },
  { key: 'no_degree_score', label: 'No-Degree', icon: GraduationCap },
  { key: 'affordability_score', label: 'Affordability', icon: Home },
];

const scoreColor = (value: number) => {
  if (value >= 75) return 'text-green-600';
  if (value >= 50) return 'text-blue-600';
  if (value >= 30) return 'text-amber-600';
  return 'text-red-500';
};

const barColor = (value: number) => {
  if (value >= 75) return 'bg-green-500';
  if (value >= 50) return 'bg-blue-500';
  if (value >= 30) return 'bg-amber-500';
  return 'bg-red-400';
};

/**
 * At-a-glance occupation "report card": Pay, Growth, AI-Safety, No-Degree and
 * Affordability scores (0-100). Turns raw salary tables into instant answers.
 */
const OccupationScoreBadges = ({ scores, className = '' }: OccupationScoreBadgesProps) => {
  const visible = SCORE_DEFS.filter((def) => scores[def.key] != null);
  if (visible.length === 0) return null;

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="text-lg">Occupation Report Card</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
          {visible.map((def) => {
            const value = scores[def.key] as number;
            const Icon = def.icon;
            return (
              <div key={def.key} className="text-center">
                <Icon className={`h-5 w-5 mx-auto mb-1 ${scoreColor(value)}`} />
                <div className={`text-2xl font-bold ${scoreColor(value)}`}>{value}</div>
                <div className="text-xs text-muted-foreground mb-2">{def.label}</div>
                <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
                  <div className={`h-full ${barColor(value)}`} style={{ width: `${value}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default OccupationScoreBadges;
